import { useEffect, useState } from 'react';
import { useSettings } from '@/api/use-settings';
import { kindColor } from '@/components/KindLabel';
import { ALL_KINDS, KindTabs } from '@/components/KindTabs';
import { TaskDetail } from '@/components/TaskDetail';
import { TaskTable } from '@/components/TaskTable';
import {
  formatMetric,
  summarize,
  totalsByKind,
  totalsByPerson,
  type Metric,
} from '@/tasks/analysis';
import { shiftMonths } from '@/tasks/calendar-range';
import {
  ALL_ASSIGNEES,
  filterByAssignee,
  filterByDateRange,
  filterByTeam,
  listAssigneeOptions,
  sortForTable,
  type Task,
  type TeamId,
} from '@/tasks/task';

/** @graph-connects none */
const SEGMENT_ON = 'bg-[var(--color-accent)] px-3 py-1 text-sm text-[var(--color-bg-surface)]';
/** @graph-connects none */
const SEGMENT_OFF =
  'bg-[var(--color-bg-surface)] px-3 py-1 text-sm text-[var(--color-text-secondary)]';

/** 担当者カードに最初から出すタスクの数 */
/** @graph-connects none */
const TOP_TASKS = 5;

/**
 * TaskAnalysis の props。
 */
export interface TaskAnalysisProps {
  /** 表示するチーム。サイドバーで選んだものが渡る */
  team: TeamId;
  /** 集計するタスク。アプリ全体で 1 つの一覧を持ち、ここへ渡す */
  tasks: readonly Task[];
  /** 初期表示の基準日（`YYYY-MM-DD`） */
  initialDate?: string;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスク分析の画面。月の合計・完了率、担当者別の積み上げ、未対応のタスク、担当者ごとの主なタスクを出す
 * @graph-connects summarize [calls] 合計と完了率を出す
 * @graph-connects totalsByKind [calls] 種別ごとに集計する
 * @graph-connects totalsByPerson [calls] 担当者ごとに集計する
 * @graph-connects filterByTeam [calls] 表示チームのタスクに絞る
 * @graph-connects filterByAssignee [calls] 担当者で絞る
 */
export function TaskAnalysis({
  team,
  tasks: allTasks,
  initialDate = '2026-09-10',
}: TaskAnalysisProps) {
  const [baseDate, setBaseDate] = useState(initialDate);
  // 担当者の選択肢はメンバー設定を元にする（タスクがまだ無い人も選べるように）
  const { settings: members } = useSettings('members');
  const [assignee, setAssignee] = useState(ALL_ASSIGNEES);
  const [kind, setKind] = useState(ALL_KINDS);
  const [metric, setMetric] = useState<Metric>('count');
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  const [selected, setSelected] = useState<Task | null>(null);

  // チームが変わると担当者も種別も顔ぶれが変わるので、絞り込みは持ち越さない
  useEffect(() => {
    setAssignee(ALL_ASSIGNEES);
    setKind(ALL_KINDS);
    setSelected(null);
  }, [team]);

  // 分析は月単位で見る（日次の粒度で傾向は読めない）
  const monthPrefix = baseDate.slice(0, 7);
  const teamTasks = filterByTeam(allTasks, team);
  const assigneeOptions = listAssigneeOptions(
    teamTasks,
    members.filter((member) => member.team === team),
  );
  const monthTasks = filterByAssignee(
    filterByDateRange(teamTasks, `${monthPrefix}-01`, `${monthPrefix}-31`),
    assignee,
  );
  const tasks = kind === ALL_KINDS ? monthTasks : monthTasks.filter((task) => task.kind === kind);

  const summary = summarize(tasks);
  const kindTotals = totalsByKind(tasks, metric);
  const people = totalsByPerson(tasks, metric);
  // 未対応は対応日の早い順。分析で見たいのは「積み残しがどれだけあるか」なので、
  // 完了したものは外して並べる
  const openTasks = sortForTable(tasks.filter((task) => task.status !== '対応済'));
  const maxPerson = Math.max(...people.map((person) => person[metric]), 1);

  // 数字カードは枠線だけにする。色は種別と状況にだけ使い、装飾には使わない
  const card = (label: string, value: string, note: string) => (
    <div
      key={label}
      className="min-w-44 flex-1 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3"
    >
      <div className="text-xs tracking-wide text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-[var(--color-text-primary)]">
        {value}
      </div>
      <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">{note}</div>
    </div>
  );

  return (
    <main className="min-w-0 flex-1 overflow-y-auto px-8 py-5">
      {/* 見出しと操作の位置はタスク管理と揃える */}
      <header className="mb-3 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="m-0 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
            タスク分析
          </h1>
          <p className="mt-0.5 mb-0 text-xs text-[var(--color-text-secondary)]">
            月単位で、誰にどの種別の仕事が寄っているかを見る
          </p>
        </div>

        <div className="flex flex-col items-stretch gap-2">
          <label htmlFor="analysis-assignee-filter" className="sr-only">
            担当で絞り込む
          </label>
          <select
            id="analysis-assignee-filter"
            value={assignee}
            onChange={(event) => {
              setAssignee(event.target.value);
            }}
            className={
              assignee === ALL_ASSIGNEES
                ? 'rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-2.5 py-1.5 text-sm text-[var(--color-text-secondary)]'
                : 'rounded border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-2.5 py-1.5 text-sm text-[var(--color-doing-text)]'
            }
          >
            <option value={ALL_ASSIGNEES}>全員</option>
            {assigneeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          aria-label="前の月"
          onClick={() => {
            setBaseDate(shiftMonths(baseDate, -1));
          }}
          className="h-7 w-7 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-sm text-[var(--color-text-secondary)]"
        >
          &lsaquo;
        </button>
        <span className="min-w-28 text-center text-base font-medium tabular-nums text-[var(--color-text-primary)]">
          {monthPrefix.replace('-', '年')}月
        </span>
        <button
          type="button"
          aria-label="次の月"
          onClick={() => {
            setBaseDate(shiftMonths(baseDate, 1));
          }}
          className="h-7 w-7 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-sm text-[var(--color-text-secondary)]"
        >
          &rsaquo;
        </button>
        <span className="text-sm text-[var(--color-text-muted)]">{tasks.length}件</span>
      </div>

      {/* 種別の絞り込みはタスク管理と同じタブを使う（同じ操作は同じ見た目に揃える） */}
      <div className="mb-4">
        <KindTabs tasks={monthTasks} selected={kind} onSelect={setKind} />
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        {card(
          '想定時間',
          `${summary.totalHours.toFixed(1)}h`,
          `対応済 ${summary.doneHours.toFixed(1)}h`,
        )}
        {card('タスク', String(summary.count), `担当 ${String(summary.memberCount)} 人`)}
        {card('一人あたり', `${summary.hoursPerPerson.toFixed(1)}h`, '想定時間 ÷ 担当人数')}
        {card(
          '完了率',
          `${summary.completionRate.toFixed(0)}%`,
          `${String(summary.doneCount)} / ${String(summary.count)} 件`,
        )}
      </div>

      {/* 種別の内訳。バーの色が何を指すかの凡例も兼ねる */}
      <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-2 text-xs">
        {kindTotals.length === 0 ? (
          <span className="text-[var(--color-text-muted)]">この月のタスクはありません</span>
        ) : (
          kindTotals.map((total) => (
            <span key={total.kind} className="text-[var(--color-text-secondary)]">
              <span
                className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                style={{ backgroundColor: kindColor(total.kind) }}
              />
              {total.kind}：
              <span className="tabular-nums">{formatMetric(total[metric], metric)}</span>
            </span>
          ))
        )}
      </div>

      <section className="mb-4 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4">
        <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          担当者ごとの量（種別で積み上げ）
        </h2>
        {people.length === 0 ? (
          <p className="m-0 py-6 text-center text-sm text-[var(--color-text-muted)]">
            この月のタスクはありません
          </p>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0">
            {people.map((person) => (
              <li key={person.assignee} className="flex items-center gap-3">
                <span className="w-24 shrink-0 truncate text-sm text-[var(--color-text-primary)]">
                  {person.assignee}
                </span>
                {/* バーの長さは一番多い人を基準にする（全体比だと差が潰れる） */}
                <span className="flex h-3 flex-1 overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                  <span
                    className="flex h-full"
                    style={{ width: `${String((person[metric] / maxPerson) * 100)}%` }}
                  >
                    {person.segments.map((segment) => (
                      <span
                        key={segment.kind}
                        title={`${segment.kind} ${formatMetric(segment[metric], metric)}`}
                        style={{
                          width: `${String((segment[metric] / (person[metric] || 1)) * 100)}%`,
                          backgroundColor: kindColor(segment.kind),
                        }}
                      />
                    ))}
                  </span>
                </span>
                <span className="w-16 shrink-0 text-right text-sm tabular-nums text-[var(--color-text-secondary)]">
                  {formatMetric(person[metric], metric)}
                </span>
                {/* 量だけでなく、どれだけ終わっているかも並べる */}
                <span className="w-20 shrink-0 text-right text-xs tabular-nums text-[var(--color-text-muted)]">
                  完了 {person.completionRate.toFixed(0)}%
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section
        aria-label="未対応のタスク"
        className="mb-4 rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-5 py-4"
      >
        <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          未対応のタスク
          <span className="ml-2 text-xs font-normal tabular-nums text-[var(--color-text-muted)]">
            {openTasks.length}件
          </span>
        </h2>
        {openTasks.length === 0 ? (
          <p className="m-0 py-6 text-center text-sm text-[var(--color-text-muted)]">
            未対応のタスクはありません
          </p>
        ) : (
          <TaskTable tasks={openTasks} onSelect={setSelected} />
        )}
      </section>

      {people.length === 0 ? null : (
        <section className="mb-3">
          <h2 className="m-0 mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
            担当者ごとの主なタスク
          </h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {people.map((person) => {
              const open = expanded.includes(person.assignee);
              const shown = open ? person.tasks : person.tasks.slice(0, TOP_TASKS);
              const rest = person.tasks.length - shown.length;
              return (
                <div
                  key={person.assignee}
                  className="rounded border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-4 py-3"
                >
                  <div className="mb-2 flex items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                      {person.assignee}
                    </span>
                    <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
                      {person.count}件 / {person.hours.toFixed(1)}h / 完了{' '}
                      {person.completionRate.toFixed(0)}%
                    </span>
                  </div>
                  <ul className="m-0 list-none space-y-1 p-0">
                    {shown.map((task) => (
                      <li key={task.id} className="flex items-baseline gap-2 text-xs">
                        <span
                          className="mt-1 h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: kindColor(task.kind) }}
                        />
                        <span
                          className={
                            task.status === '対応済'
                              ? 'min-w-0 flex-1 truncate text-[var(--color-text-muted)] line-through'
                              : 'min-w-0 flex-1 truncate text-[var(--color-text-primary)]'
                          }
                          title={task.title}
                        >
                          {task.title}
                        </span>
                        <span className="shrink-0 tabular-nums text-[var(--color-text-muted)]">
                          {task.estimatedHours === undefined || task.estimatedHours === null
                            ? ''
                            : `${task.estimatedHours.toFixed(2)}h`}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {rest <= 0 && !open ? null : (
                    <button
                      type="button"
                      onClick={() => {
                        setExpanded(
                          open
                            ? expanded.filter((name) => name !== person.assignee)
                            : [...expanded, person.assignee],
                        );
                      }}
                      className="mt-2 w-full rounded border border-[var(--color-border-subtle)] py-1 text-xs text-[var(--color-text-secondary)]"
                    >
                      {open ? '閉じる' : `あと ${String(rest)} 件を見る`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {selected === null ? null : (
        <TaskDetail
          task={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}

      {/* 件数／想定時間の切り替えは、他の画面と同じ右下（画面の一番下）に置く */}
      <div className="mt-4 flex justify-end">
        <div
          role="tablist"
          aria-label="集計の単位"
          className="inline-flex overflow-hidden rounded border border-[var(--color-border-subtle)]"
        >
          {(['count', 'hours'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={metric === mode}
              onClick={() => {
                setMetric(mode);
              }}
              className={metric === mode ? SEGMENT_ON : SEGMENT_OFF}
            >
              {mode === 'count' ? '件数' : '想定時間'}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
